import { gql, useMutation } from '@apollo/client'
import { yupResolver } from '@/gf/lib/yupResolver'
import { GraphQLError } from 'graphql'
import { useForm, Controller } from 'react-hook-form'
import * as yup from 'yup'

import LocationGroupSelector from '@/buyers/components/LocationGroupSelector'
import useSession from '@/buyers/hooks/useSession'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import { useEffect } from 'react'

import ModalForm from '@/gf/components/ModalForm'
import TextField from '@/gf/components/TextField'

const EDIT_USER = gql`
  mutation EditOrgUser(
    $userId: String
    $title: String
    $name: String
    $phoneNumber: String
    $shippingLocationGroupIds: [ID!]
    $purchasingLimit: Int
  ) {
    editOrgUser(
      userId: $userId
      title: $title
      name: $name
      phoneNumber: $phoneNumber
      shippingLocationGroupIds: $shippingLocationGroupIds
      purchasingLimit: $purchasingLimit
    ) {
      user {
        id
      }
    }
  }
`

const orgUserSchema = yup.object({
  title: yup.string().optional().nullable().label('Title'),
  name: yup.string().optional().nullable().label('Name'),
  phoneNumber: yup.string().optional().nullable().label('Phone number'),
  shippingLocationGroupIds: yup.array().of(yup.string()).optional().label('Location groups'),
  purchasingLimit: yup
    .number()
    .transform((value, original) => (original === '' || original === null ? null : value))
    .integer()
    .min(0)
    .optional()
    .nullable()
    .label('Purchasing limit'),
})

type OrgUserForm = {
  title: string
  name: string
  phoneNumber: string
  shippingLocationGroupIds?: string[]
  purchasingLimit?: number | null
}

const EditOrgUserModalForm = ({ open, onClose, selected, refetch }) => {
  const [_msgs, msgsMgr] = useMsgs()
  const [spinnerLive, spinnerToggler] = useToggle()
  const { user: sessionUser, features } = useSession()
  const showPurchasingLimit = features.zachryIntegrationEnabled && sessionUser.can.manageUsers
  const selectedOrgUser = selected
    ? {
        ...selected,
        shippingLocationGroupIds: selected.shippingLocationGroups?.map((g) => g.id) || [],
        purchasingLimit: selected.orgUser?.purchasingLimit ?? null,
      }
    : {}

  const form = useForm<OrgUserForm>({
    shouldUnregister: true,
    defaultValues: selectedOrgUser,
    resolver: yupResolver(orgUserSchema),
  })

  useEffect(() => {
    form.reset(selectedOrgUser)
  }, [selected])

  const [editUserMutation] = useMutation(EDIT_USER)

  const editOrgUser = (formData, event) => {
    event?.preventDefault()

    spinnerToggler.on()

    editUserMutation({
      variables: {
        userId: selected.id,
        title: formData.title,
        name: formData.name,
        phoneNumber: formData.phoneNumber,
        shippingLocationGroupIds: formData.shippingLocationGroupIds || [],
        // Only send when editable; explicit null clears the limit (unlimited)
        ...(showPurchasingLimit ? { purchasingLimit: formData.purchasingLimit ?? null } : {}),
      },
    })
      .then(() => {
        msgsMgr.add('User updated', 'positive')

        return refetch().then(() => {
          form.reset()

          return Promise.resolve().then(onClose)
        })
      })
      .catch((e: GraphQLError) => {
        if (e.message) {
          msgsMgr.add(e.message, 'negative')
        } else {
          msgsMgr.add('Error editing user', 'negative')
        }

        onClose()
      })
      .finally(spinnerToggler.off)
  }

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title="Edit User"
      submitButtonDisabled={spinnerLive}
      submitButtonShowSpinner={spinnerLive}
      onSubmit={form.handleSubmit(editOrgUser)}
    >
      <div className="flex flex-col space-y-4">
        <TextField
          label="Phone number (optional)"
          {...form.register('phoneNumber')}
          errors={form.formState.errors.phoneNumber?.message}
        />
        <TextField
          label="Title (optional)"
          {...form.register('title')}
          errors={form.formState.errors.title?.message}
        />
        <TextField
          label="Name (optional)"
          {...form.register('name')}
          errors={form.formState.errors.name?.message}
        />
        <Controller
          name="shippingLocationGroupIds"
          control={form.control}
          render={({ field }) => (
            <LocationGroupSelector
              value={field.value || []}
              onChange={field.onChange}
              label="Location Groups (optional)"
            />
          )}
        />
        {showPurchasingLimit && (
          <TextField
            label="Purchasing Limit ($)"
            placeholder="Leave blank for unlimited"
            type="number"
            min={0}
            {...form.register('purchasingLimit')}
            errors={form.formState.errors.purchasingLimit?.message}
          />
        )}
      </div>
    </ModalForm>
  )
}

export default EditOrgUserModalForm
